import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getEmployeeDetails, listEmployeeDocs, docUrl } from '../lib/employeeDetails';
import { loadPrefs, savePrefs } from '../lib/prefs';
import { subscribePush, pushSupported, notifPerm, isStandalone, isIOS } from '../lib/push';

const inp = 'mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-sky-200';
const fmtDate = (d) => (d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' }) : '');
const Row = ({ k, v }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className="text-xs text-gray-400 shrink-0">{k}</span>
    <span className={`text-sm text-right break-words min-w-0 ${v ? 'text-gray-800' : 'text-gray-300'}`}>{v || '—'}</span>
  </div>
);
const Card = ({ title, children }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-3.5">
    {title && <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1">{title}</p>}
    {children}
  </div>
);
const Toggle = ({ on, onChange, label, hint, disabled }) => (
  <div className={`flex items-center justify-between gap-3 py-2 ${disabled ? 'opacity-60' : ''}`}>
    <span className="text-sm text-gray-700 min-w-0">{label}{hint && <span className="block text-[11px] text-gray-400">{hint}</span>}</span>
    <button type="button" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)}
      className={`shrink-0 w-10 h-6 rounded-full transition-colors relative ${on ? 'bg-sky-600' : 'bg-gray-200'}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
    </button>
  </div>
);

function Datos({ profile, showToast }) {
  const [d, setD] = useState(null); const [f, setF] = useState({}); const [busy, setBusy] = useState(false);
  useEffect(() => { getEmployeeDetails(profile.id).then((x) => { setD(x); setF(x); }).catch(() => { setD({}); setF({}); }); }, [profile.id]);
  if (!d) return <p className="text-sm text-gray-400 text-center py-8">Cargando…</p>;
  const dirty = ['phone', 'address', 'emergency_name', 'emergency_phone'].some((k) => (f[k] || '') !== (d[k] || ''));
  const save = async () => {
    setBusy(true);
    try {
      const row = { employee_id: profile.id, updated_at: new Date().toISOString() };
      ['phone', 'address', 'emergency_name', 'emergency_phone'].forEach((k) => { row[k] = (f[k] || '').trim() || null; });
      const { error } = await supabase.from('employee_details').upsert(row, { onConflict: 'employee_id' });
      if (error) throw new Error(error.message);
      setD({ ...d, ...row }); showToast?.('Datos guardados');
    } catch (e) { showToast?.(e.message || 'No se pudo guardar', 'error'); }
    setBusy(false);
  };
  return (
    <div className="space-y-3">
      <Card title="Mis datos">
        <div className="divide-y divide-gray-50">
          <Row k="Nombre" v={profile.name} /><Row k="Email" v={profile.email} />
          <Row k="Puesto" v={d.job_title} /><Row k="DNI" v={d.document_id} />
          <Row k="Nacimiento" v={fmtDate(d.birth_date)} /><Row k="Ingreso" v={fmtDate(profile.hire_date)} />
        </div>
        <p className="text-[11px] text-gray-400 mt-2">Si algo de esto está mal, pedile a tu empleador que lo corrija.</p>
      </Card>
      <Card title="Contacto (podés editarlo)">
        <div className="space-y-2.5 pt-1">
          {[['phone', 'Teléfono'], ['address', 'Domicilio'], ['emergency_name', 'Contacto de emergencia'], ['emergency_phone', 'Tel. de emergencia']].map(([k, l]) => (
            <label key={k} className="block text-xs text-gray-500">{l}<input className={inp} value={f[k] || ''} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} /></label>
          ))}
          {dirty && <button type="button" onClick={save} disabled={busy} className="w-full text-sm font-semibold text-white bg-sky-600 py-2.5 rounded-xl disabled:opacity-50">{busy ? 'Guardando…' : 'Guardar'}</button>}
        </div>
      </Card>
    </div>
  );
}

function Docs({ profile, showToast }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => { listEmployeeDocs(profile.id).then(setDocs).catch(() => setDocs([])); }, [profile.id]);
  const open = async (d) => { try { window.open(await docUrl(d.path), '_blank', 'noopener'); } catch (e) { showToast?.(e.message, 'error'); } };
  return (
    <Card title="Mis documentos">
      {docs === null ? <p className="text-sm text-gray-400 py-3">Cargando…</p> : docs.length === 0 ? <p className="text-sm text-gray-400 py-3">Todavía no hay documentos. Tu empleador puede subir tu contrato u otros archivos.</p> : (
        <div className="divide-y divide-gray-50">
          {docs.map((d) => (
            <button key={d.id} type="button" onClick={() => open(d)} className="w-full text-left py-2.5">
              <p className="text-sm text-gray-800 truncate">{d.name}</p>
              <p className="text-[11px] text-gray-400">{d.kind || 'Documento'} · {new Date(d.created_at).toLocaleDateString('es-AR')}</p>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

const CATS = [
  ['leaves', 'Justificaciones', 'Cuando se aprueba o rechaza una falta que justificaste'],
  ['pay', 'Sueldos y adelantos', 'Adelantos, pagos y liquidaciones'],
  ['calls', 'Convocatorias', 'Cuando te convocan para un día'],
];

function Avisos({ profile, showToast }) {
  const [n, setN] = useState(null);
  const [perm, setPerm] = useState(notifPerm());
  useEffect(() => { loadPrefs(profile.id).then((p) => setN(p.notif || {})).catch(() => setN({})); }, [profile.id]);
  if (!n) return <p className="text-sm text-gray-400 text-center py-8">Cargando…</p>;
  const rem = { push: true, entry: true, exit: true, lead: 10, ...(n.reminders || {}) };
  const persist = async (next) => { setN(next); try { await savePrefs(profile.id, { notif: next }); } catch (e) { showToast?.(e.message || 'No se pudo guardar', 'error'); } };
  const setRem = (patch) => persist({ ...n, reminders: { ...rem, ...patch } });
  const catOn = (k) => (n[k]?.push ?? true);
  const enable = async () => {
    try {
      if (pushSupported() && Notification.permission === 'default') await Notification.requestPermission();
      setPerm(notifPerm());
      if (notifPerm() === 'granted') { await subscribePush(); showToast?.('Notificaciones activadas'); }
    } catch (e) { showToast?.('No se pudo activar', 'error'); }
  };
  return (
    <div className="space-y-3">
      <Card title="Notificaciones en este dispositivo">
        {perm === 'granted'
          ? <p className="text-sm text-emerald-700">Activadas en este dispositivo.</p>
          : perm === 'denied'
            ? <p className="text-sm text-amber-700">Las bloqueaste. Para activarlas, habilitá las notificaciones de esta app en los ajustes de tu celular.</p>
            : !pushSupported() || (isIOS() && !isStandalone())
              ? <p className="text-sm text-gray-500">En iPhone hay que instalar la app (Compartir → Agregar a inicio) para recibir notificaciones.</p>
              : <button type="button" onClick={enable} className="w-full text-sm font-semibold text-white bg-sky-600 py-2.5 rounded-xl">Activar notificaciones</button>}
      </Card>
      <Card title="Recordatorios de asistencia">
        <Toggle on={!!rem.push} onChange={(v) => setRem({ push: v })} label="Recibir recordatorios" hint="Te avisamos antes de tu turno y al terminar si te falta marcar." />
        {rem.push && (<>
          <Toggle on={!!rem.entry} onChange={(v) => setRem({ entry: v })} label="Entrada" />
          <Toggle on={!!rem.exit} onChange={(v) => setRem({ exit: v })} label="Salida" />
          <label className="flex items-center justify-between gap-3 py-2 text-sm text-gray-700">Avisarme antes de la entrada
            <select value={rem.lead} onChange={(e) => setRem({ lead: Number(e.target.value) })} className="px-3 py-1.5 rounded-xl border border-gray-200 text-sm bg-white">
              {[5, 10, 15, 30].map((m) => <option key={m} value={m}>{m} min</option>)}
            </select>
          </label>
        </>)}
      </Card>
      <Card title="Otros avisos">
        {CATS.map(([k, l, h]) => <Toggle key={k} on={catOn(k)} onChange={(v) => persist({ ...n, [k]: { ...(n[k] || {}), push: v } })} label={l} hint={h} />)}
        <Toggle on disabled label="Seguridad" hint="Marcación desde otro dispositivo. Siempre activo." />
      </Card>
    </div>
  );
}

// bio: tarjeta de huella + datos de la cuenta (ya existentes en EmployeeView)
export default function EmployeeFicha({ profile, showToast, bio }) {
  const [pane, setPane] = useState('datos');
  const P = [['datos', 'Datos'], ['docs', 'Documentos'], ['avisos', 'Avisos'], ['huella', 'Huella']];
  return (
    <div className="space-y-3">
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
        {P.map(([id, l]) => <button key={id} onClick={() => setPane(id)} className={`shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold ${pane === id ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-500'}`}>{l}</button>)}
      </div>
      {pane === 'datos' && <Datos profile={profile} showToast={showToast} />}
      {pane === 'docs' && <Docs profile={profile} showToast={showToast} />}
      {pane === 'avisos' && <Avisos profile={profile} showToast={showToast} />}
      {pane === 'huella' && bio}
    </div>
  );
}
